/**
 * Translation Cache Utility (L1 - Browser)
 * 
 * Provides in-memory and localStorage caching for translations.
 * This is the first tier of the three-tier caching strategy:
 * - L1: Browser (this file) - fastest, session-based
 * - L2: Redis (backend) - shared across users
 * - L3: Database (backend) - persistent storage
 */

import { Language } from '../contexts/LanguageContext';

/**
 * Cache entry with metadata
 */
interface CacheEntry {
  value: string;
  timestamp: number;
  locale: Language;
}

/**
 * Cache configuration
 */
interface CacheConfig {
  /** TTL in milliseconds (default: 1 hour) */
  ttl: number;
  /** Max entries in memory cache (default: 500) */
  maxMemoryEntries: number;
  /** localStorage key prefix */
  storagePrefix: string;
  /** Enable localStorage persistence */
  persistToStorage: boolean;
}

const DEFAULT_CONFIG: CacheConfig = {
  ttl: 60 * 60 * 1000, // 1 hour
  maxMemoryEntries: 500,
  storagePrefix: 'i18n_cache_',
  persistToStorage: true,
};

/**
 * In-memory cache store
 */
const memoryCache = new Map<string, CacheEntry>();

/**
 * Generate cache key
 */
function getCacheKey(locale: Language, key: string): string {
  return `${locale}:${key}`;
}

/**
 * Translation Cache
 */
export const translationCache = {
  config: { ...DEFAULT_CONFIG },

  /**
   * Configure the cache
   */
  configure(options: Partial<CacheConfig>): void {
    this.config = { ...this.config, ...options };
  },

  /**
   * Get a cached translation
   * 
   * @param locale - Language code
   * @param key - Translation key
   * @returns Cached value or null if not found/expired
   */
  get(locale: Language, key: string): string | null {
    const cacheKey = getCacheKey(locale, key);
    
    // Check memory cache first
    const memoryEntry = memoryCache.get(cacheKey);
    if (memoryEntry) {
      // Check if expired
      if (Date.now() - memoryEntry.timestamp < this.config.ttl) {
        return memoryEntry.value;
      }
      // Expired - remove from memory
      memoryCache.delete(cacheKey);
    }

    // Check localStorage if enabled
    if (this.config.persistToStorage) {
      try {
        const stored = localStorage.getItem(this.config.storagePrefix + cacheKey);
        if (stored) {
          const entry: CacheEntry = JSON.parse(stored);
          // Check if expired
          if (Date.now() - entry.timestamp < this.config.ttl) {
            // Restore to memory cache
            memoryCache.set(cacheKey, entry);
            return entry.value;
          }
          // Expired - remove from storage
          localStorage.removeItem(this.config.storagePrefix + cacheKey);
        }
      } catch (e) {
        // localStorage might be unavailable or corrupted
        console.warn('Failed to read from localStorage:', e);
      }
    }

    return null;
  },

  /**
   * Get multiple cached translations
   * 
   * @param locale - Language code
   * @param keys - Translation keys
   * @returns Object with cached values (missing keys are not included)
   */
  getMany(locale: Language, keys: string[]): Record<string, string> {
    const result: Record<string, string> = {};
    
    for (const key of keys) {
      const value = this.get(locale, key);
      if (value !== null) {
        result[key] = value;
      }
    }
    
    return result;
  },

  /**
   * Set a cached translation
   * 
   * @param locale - Language code
   * @param key - Translation key
   * @param value - Translated text
   */
  set(locale: Language, key: string, value: string): void {
    const cacheKey = getCacheKey(locale, key);
    const entry: CacheEntry = {
      value,
      timestamp: Date.now(),
      locale,
    };

    // Enforce max entries in memory cache
    if (memoryCache.size >= this.config.maxMemoryEntries) {
      // Remove oldest entry (first in map)
      const firstKey = Array.from(memoryCache.keys())[0];
      if (firstKey) {
        memoryCache.delete(firstKey);
      }
    }

    // Add to memory cache
    memoryCache.set(cacheKey, entry);

    // Persist to localStorage if enabled
    if (this.config.persistToStorage) {
      try {
        localStorage.setItem(
          this.config.storagePrefix + cacheKey,
          JSON.stringify(entry)
        );
      } catch (e) {
        // localStorage might be full or unavailable
        console.warn('Failed to persist to localStorage:', e);
      }
    }
  },

  /**
   * Set multiple cached translations
   * 
   * @param locale - Language code
   * @param translations - Object of key → value pairs
   */
  setMany(locale: Language, translations: Record<string, string>): void {
    for (const [key, value] of Object.entries(translations)) {
      this.set(locale, key, value);
    }
  },

  /**
   * Remove a cached translation
   * 
   * @param locale - Language code
   * @param key - Translation key
   */
  remove(locale: Language, key: string): void {
    const cacheKey = getCacheKey(locale, key);
    
    memoryCache.delete(cacheKey);
    
    if (this.config.persistToStorage) {
      try {
        localStorage.removeItem(this.config.storagePrefix + cacheKey);
      } catch (e) {
        console.warn('Failed to remove from localStorage:', e);
      }
    }
  },

  /**
   * Clear all cached translations for a locale
   * 
   * @param locale - Language code (optional, clears all if not provided)
   */
  clear(locale?: Language): void {
    if (locale) {
      // Clear specific locale
      const prefix = `${locale}:`;
      
      // Clear from memory - use Array.from() to avoid iterator issues
      for (const key of Array.from(memoryCache.keys())) {
        if (key.startsWith(prefix)) {
          memoryCache.delete(key);
        }
      }
      
      // Clear from localStorage
      if (this.config.persistToStorage) {
        try {
          const storagePrefix = this.config.storagePrefix + prefix;
          for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (key && key.startsWith(storagePrefix)) {
              localStorage.removeItem(key);
            }
          }
        } catch (e) {
          console.warn('Failed to clear localStorage:', e);
        }
      }
    } else {
      // Clear all
      memoryCache.clear();
      
      if (this.config.persistToStorage) {
        try {
          for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (key && key.startsWith(this.config.storagePrefix)) {
              localStorage.removeItem(key);
            }
          }
        } catch (e) {
          console.warn('Failed to clear localStorage:', e);
        }
      }
    }
  },

  /**
   * Get cache statistics
   */
  getStats(): { memorySize: number; storageSize: number } {
    let storageSize = 0;
    
    if (this.config.persistToStorage) {
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith(this.config.storagePrefix)) {
            storageSize++;
          }
        }
      } catch (e) {
        // Ignore
      }
    }
    
    return {
      memorySize: memoryCache.size,
      storageSize,
    };
  },

  /**
   * Check if a key is cached
   */
  has(locale: Language, key: string): boolean {
    return this.get(locale, key) !== null;
  },
};

export default translationCache;